// src/mobile/MobileCaseStudyPage.js
// Mobile Case Study detail page with premium showcase experience

import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import {
  ArrowLeft,
  TrendingUp,
  Building2,
  Briefcase,
  MapPin,
  Quote,
  AlertCircle,
} from 'lucide-react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import MobileNavbar from './MobileNavbar';
import MobileResourceCard from './MobileResourceCard';
import { useCaseStudy, useResourcesList } from '../hooks/useContentful';
import './styles/mobile-case-study.css';

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
};

// Rich text rendering options for mobile
const richTextOptions = (assetMap = {}) => ({
  renderNode: {
    [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
    [BLOCKS.HEADING_3]: (node, children) => <h3>{children}</h3>,
    [BLOCKS.HEADING_4]: (node, children) => <h4>{children}</h4>,
    [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
    [BLOCKS.UL_LIST]: (node, children) => <ul>{children}</ul>,
    [BLOCKS.OL_LIST]: (node, children) => <ol>{children}</ol>,
    [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
    [BLOCKS.QUOTE]: (node, children) => <blockquote>{children}</blockquote>,
    [BLOCKS.HR]: () => <hr />,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const assetId = node.data?.target?.sys?.id;
      const asset = assetMap[assetId];
      if (!asset) return null;
      return (
        <figure>
          <img
            src={asset.url}
            alt={asset.description || asset.title || ''}
            loading="lazy"
          />
          {asset.description && <figcaption>{asset.description}</figcaption>}
        </figure>
      );
    },
    [INLINES.HYPERLINK]: (node, children) => (
      <a href={node.data.uri} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
  },
});

const MobileCaseStudyPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const { data: caseStudy, loading, error } = useCaseStudy(slug);
  const { data: allResources } = useResourcesList();

  // Get related case studies
  const relatedCaseStudies = React.useMemo(() => {
    if (!caseStudy || !allResources) return [];
    const caseStudyTopicNames = caseStudy.topics?.map(t => t?.name).filter(Boolean) || [];
    return allResources
      .filter(
        (r) =>
          r.slug !== slug &&
          r.type === 'caseStudy' &&
          (r.industry === caseStudy.industry ||
            r.topics?.some((t) => caseStudyTopicNames.includes(t?.name)))
      )
      .slice(0, 2);
  }, [caseStudy, allResources, slug]);

  // Build asset map for rich text (for any section that has assets)
  const assetMap = React.useMemo(() => {
    // Combine assets from all rich text fields
    const allAssets = [
      ...(caseStudy?.challengeAssets || []),
      ...(caseStudy?.approachAssets || []),
      ...(caseStudy?.resultsAssets || []),
    ];
    return allAssets.reduce((map, asset) => {
      if (asset?.sys?.id) {
        map[asset.sys.id] = asset;
      }
      return map;
    }, {});
  }, [caseStudy]);

  // Handle back navigation
  const handleBack = () => {
    navigate('/resources');
  };

  // Render loading skeleton
  const renderSkeleton = () => (
    <div className="mobile-case-study__loading">
      <div className="mobile-case-study__skeleton-back" />
      <div className="mobile-case-study__skeleton-badge" />
      <div className="mobile-case-study__skeleton-meta">
        <div className="mobile-case-study__skeleton-meta-item" />
        <div className="mobile-case-study__skeleton-meta-item" />
      </div>
      <div className="mobile-case-study__skeleton-title" />
      <div className="mobile-case-study__skeleton-title" />
      <div className="mobile-case-study__skeleton-outcome" />
      <div className="mobile-case-study__skeleton-metrics">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="mobile-case-study__skeleton-metric" />
        ))}
      </div>
      <div className="mobile-case-study__skeleton-image" />
      <div className="mobile-case-study__skeleton-body">
        {[1, 2, 3].map((i) => (
          <div key={i} className="mobile-case-study__skeleton-section">
            <div className="mobile-case-study__skeleton-section-title" />
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="mobile-case-study__skeleton-paragraph" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  // Render error state
  if (error) {
    return (
      <div className="mobile-case-study">
        <MobileNavbar />
        <div className="mobile-case-study__error">
          <div className="mobile-case-study__error-icon">
            <AlertCircle size={28} />
          </div>
          <h1 className="mobile-case-study__error-title">Case Study Not Found</h1>
          <p className="mobile-case-study__error-text">
            We couldn't find the case study you're looking for. It may have been moved or deleted.
          </p>
          <Link to="/resources" className="mobile-case-study__error-btn">
            <ArrowLeft size={18} />
            Back to Resources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-case-study">
      {/* Progress Bar */}
      <motion.div className="mobile-case-study__progress" style={{ scaleX }} />

      <MobileNavbar />

      {loading ? (
        renderSkeleton()
      ) : caseStudy ? (
        <>
          {/* Header */}
          <header className="mobile-case-study__header">
            <button className="mobile-case-study__back" onClick={handleBack}>
              <ArrowLeft size={18} />
              Back to Resources
            </button>

            <span className="mobile-case-study__badge">Case Study</span>

            {/* Client Info */}
            <div className="mobile-case-study__client-info">
              {caseStudy.clientName && (
                <span className="mobile-case-study__client">
                  <Building2 />
                  {caseStudy.clientName}
                </span>
              )}
              {caseStudy.industry && (
                <span className="mobile-case-study__industry">
                  <Briefcase />
                  {caseStudy.industry}
                </span>
              )}
              {caseStudy.region && (
                <span className="mobile-case-study__region">
                  <MapPin />
                  {caseStudy.region}
                </span>
              )}
            </div>

            <h1 className="mobile-case-study__title">{caseStudy.title}</h1>

            {/* Headline Outcome */}
            {caseStudy.headlineOutcome && (
              <div className="mobile-case-study__outcome">
                <TrendingUp />
                <span>{caseStudy.headlineOutcome}</span>
              </div>
            )}

            {caseStudy.summary && (
              <p className="mobile-case-study__intro">{caseStudy.summary}</p>
            )}

            <span className="mobile-case-study__date">
              {formatDate(caseStudy.publishedAt)}
            </span>
          </header>

          {/* Metrics */}
          {caseStudy.metrics?.length > 0 && (
            <div className="mobile-case-study__metrics">
              {caseStudy.metrics.map((metric, idx) => (
                <motion.div
                  key={idx}
                  className="mobile-case-study__metric"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <span className="mobile-case-study__metric-value">
                    {metric.value}
                  </span>
                  <span className="mobile-case-study__metric-label">
                    {metric.label}
                  </span>
                  {metric.description && (
                    <span className="mobile-case-study__metric-desc">
                      {metric.description}
                    </span>
                  )}
                </motion.div>
              ))}
            </div>
          )}

          {/* Hero Image */}
          {caseStudy.heroImage && (
            <div className="mobile-case-study__hero">
              <img
                src={caseStudy.heroImage}
                alt={caseStudy.title}
                className="mobile-case-study__hero-image"
              />
            </div>
          )}

          {/* Body Sections */}
          <div className="mobile-case-study__body">
            {/* Challenge Section */}
            {caseStudy.challenge && (
              <section className="mobile-case-study__section">
                <h2 className="mobile-case-study__section-title">
                  <span className="mobile-case-study__section-number">01</span>
                  The Challenge
                </h2>
                <div className="mobile-rich-text">
                  {documentToReactComponents(
                    caseStudy.challenge,
                    richTextOptions(assetMap)
                  )}
                </div>
              </section>
            )}

            {/* Approach Section */}
            {caseStudy.approach && (
              <section className="mobile-case-study__section">
                <h2 className="mobile-case-study__section-title">
                  <span className="mobile-case-study__section-number">02</span>
                  Our Approach
                </h2>
                <div className="mobile-rich-text">
                  {documentToReactComponents(
                    caseStudy.approach,
                    richTextOptions(assetMap)
                  )}
                </div>
              </section>
            )}

            {/* Results Section */}
            {caseStudy.results && (
              <section className="mobile-case-study__section">
                <h2 className="mobile-case-study__section-title">
                  <span className="mobile-case-study__section-number">03</span>
                  Results & Impact
                </h2>
                <div className="mobile-rich-text">
                  {documentToReactComponents(
                    caseStudy.results,
                    richTextOptions(assetMap)
                  )}
                </div>
              </section>
            )}
          </div>

          {/* Testimonial */}
          {caseStudy.testimonial && (
            <div className="mobile-case-study__testimonial">
              <Quote size={28} className="mobile-case-study__testimonial-icon" />
              <p className="mobile-case-study__testimonial-quote">
                "{caseStudy.testimonial.quote}"
              </p>
              <div className="mobile-case-study__testimonial-author">
                <span className="mobile-case-study__testimonial-name">
                  {caseStudy.testimonial.name}
                </span>
                <span className="mobile-case-study__testimonial-title">
                  {caseStudy.testimonial.title}
                </span>
              </div>
            </div>
          )}

          {/* Technologies Used */}
          {caseStudy.technologies?.length > 0 && (
            <div className="mobile-case-study__tech">
              <h3 className="mobile-case-study__tech-title">Technologies Used</h3>
              <div className="mobile-case-study__tech-list">
                {caseStudy.technologies.map((tech, idx) => (
                  <span key={idx} className="mobile-case-study__tech-item">
                    {tech.icon && (
                      <img
                        src={tech.icon}
                        alt={tech.name}
                        className="mobile-case-study__tech-icon"
                      />
                    )}
                    {tech.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {caseStudy.topics?.length > 0 && (
            <div className="mobile-case-study__tags">
              {caseStudy.topics.map((topic) => (
                <span key={topic.id || topic.name} className="mobile-case-study__tag">
                  {topic.name}
                </span>
              ))}
            </div>
          )}

          {/* Related Case Studies */}
          {relatedCaseStudies.length > 0 && (
            <section className="mobile-case-study__related">
              <h2 className="mobile-case-study__related-title">
                Related Case Studies
              </h2>
              <div className="mobile-case-study__related-list">
                {relatedCaseStudies.map((resource, index) => (
                  <MobileResourceCard
                    key={resource.slug}
                    resource={resource}
                    variant="compact"
                    index={index}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      ) : null}
    </div>
  );
};

export default MobileCaseStudyPage;
